/**
 * 重连后「服务端说这条连接不在房间里，界面却还停在房间里」的自愈。
 *
 * 由来（2026-09-17 本地验收）：房主那页在后台放了几分钟，切回来还显示等待室，
 * 点什么都回「尚未加入任何房间」，只有刷新才好。服务端现在能把换票前的房间绑定接过来；
 * 这里兜住它接不上的情形（旧会话已被回收、房间已经没了……）：
 *   - 回得去：用同一个房间码再 `joinRoom` —— 和刷新页面走同一条路，座位、筹码按账号认回来；
 *   - 回不去：退回大厅并说清原因。绝不留一张点什么都没反应的旧牌桌。
 *
 * ★ 只认网关那条带 `resumeRoomId` 的 E_NOT_IN_ROOM（见 shared 的 `resumeLostRoomId`）。
 *   房间里也会回这个码（要踢的人已经走了），那些不该触发重进房。
 */
import { resumeLostRoomId, type ClientMessage, type ErrorCode, type ServerMessage } from '@poker/shared'

import { describeRoomError } from '../lobby/lobbyFlow.js'
import { currentNickname } from '../lobby/useAuth.js'
import { useNetStore } from '../transport/applyMessage.js'

/** 只要传输层的这两样 */
export interface RebindLink {
  send(msg: ClientMessage): number
  onMessage(cb: (m: ServerMessage) => void): () => void
}

/** 重进房要用的本机房间信息 */
export interface RebindTarget {
  readonly roomId: string
  readonly code: string
  readonly nickname: string
  readonly asSpectator: boolean
}

export interface RebindDeps {
  /** 界面上正停着的房间；不在房间里（或房间已结束、正在看结算）返回 null */
  readonly current?: () => RebindTarget | null
  /** 回不去了：清掉房间态回大厅，并告诉玩家原因 */
  readonly giveUp: (text: string) => void
  /** 这条错误由本模块接手了，别再让房间里的通用提示弹一遍 */
  readonly clearError?: (err: ErrorMessage) => void
}

type ErrorMessage = Extract<ServerMessage, { type: 'error' }>

/** 只清**这一条**：帧可能被判成缺口没落进 store，那时 lastError 是别的事，不能顺手抹掉 */
function clearIfSame(err: ErrorMessage): void {
  const net = useNetStore.getState()
  const cur = net.lastError
  if (cur !== null && cur.code === err.code && cur.refCid === err.refCid) net.clearError()
}

export function currentRoomTarget(): RebindTarget | null {
  const s = useNetStore.getState()
  const view = s.view
  if (view === null || s.closedRoom !== null || view.room.phase === 'closed') return null
  const me = view.viewer.accountId
  // 坐着的人沿用桌上那个名字：服务端见到与账号昵称不同的名字才会覆盖，传桌上的名字就原样保留
  const seated = me === null ? null : view.seats.find((seat) => seat.occupant?.accountId === me)
  return {
    roomId: view.room.id,
    code: view.room.code,
    nickname: seated?.occupant?.nickname ?? currentNickname(),
    asSpectator: view.viewer.role === 'spectator',
  }
}

/** 重进房失败时说什么。房间码是本机记着的，「再对一遍房间码」在这里是句错话 */
export function rebindFailureText(code: ErrorCode, message: string): string {
  if (code === 'E_ROOM_NOT_FOUND') return '这个房间已经结束了'
  return describeRoomError(code, message, 'join')
}

export function startRoomRebind(link: RebindLink, deps: RebindDeps): () => void {
  const current = deps.current ?? currentRoomTarget
  const clearError = deps.clearError ?? clearIfSame
  /** 正在飞的那条 joinRoom */
  let pending: { readonly cid: number; readonly roomId: string } | null = null

  return link.onMessage((m) => {
    switch (m.type) {
      case 'welcome':
        // 换了连接：上一条连接上的 joinRoom 已经没有回音了
        pending = null
        return
      case 'roomSnapshot':
        if (pending !== null && pending.roomId === m.room.id) pending = null
        return
      case 'error':
        break
      default:
        return
    }

    const lost = resumeLostRoomId(m)
    if (lost !== null) {
      clearError(m)
      const target = current()
      // 界面已经不在那个房间了（离开了 / 进了别的房 / 正看结算）：没什么要回的
      if (target === null || target.roomId !== lost || pending !== null) return
      const cid = link.send({
        type: 'joinRoom',
        code: target.code,
        nickname: target.nickname,
        ...(target.asSpectator ? { asSpectator: true } : {}),
      })
      pending = { cid, roomId: lost }
      return
    }

    if (pending === null || m.refCid !== pending.cid) return
    pending = null
    clearError(m)
    // 这条连接其实已经在房里了（同时有别的进房请求先到）：没有要收拾的
    if (m.code === 'E_ALREADY_IN_ROOM') return
    deps.giveUp(rebindFailureText(m.code, m.message))
  })
}
